<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import { channelColor } from '../helpers/timeline';
import NotifySettings from './NotifySettings.vue';

const props = defineProps({
  channels: { type: Array, required: true },
  activeChannelId: { type: Number, default: null },
  canCreate: { type: Boolean, default: false },
  onlineAgents: { type: Array, default: () => [] },
});

const emit = defineEmits(['select', 'create']);

const { t } = useI18n();
const store = useStore();
const search = ref('');

const visibleChannels = computed(() => {
  const term = search.value.trim().toLowerCase();
  const list = term
    ? props.channels.filter(channel =>
        channel.name.toLowerCase().includes(term)
      )
    : props.channels;
  return [...list].sort((a, b) => a.name.localeCompare(b.name));
});

const unreadFor = channel =>
  channel.id === props.activeChannelId
    ? 0
    : store.getters['teamChat/getUnread'](channel.id);
</script>

<template>
  <aside
    class="flex-col h-full border-e border-n-weak bg-n-solid-1 w-full md:w-72 md:flex-shrink-0"
  >
    <header
      class="relative flex items-center justify-between gap-2 px-4 pt-4 pb-3"
    >
      <div class="min-w-0">
        <h2 class="text-base font-semibold text-n-slate-12">
          {{ t('TEAM_CHAT.TITLE') }}
        </h2>
        <p class="text-xs text-n-slate-11">{{ t('TEAM_CHAT.SUBTITLE') }}</p>
      </div>
      <div class="flex items-center gap-1">
      <NotifySettings />
      <Button
        v-if="canCreate"
        icon="i-lucide-plus"
        size="sm"
        variant="faded"
        color="slate"
        :title="t('TEAM_CHAT.NEW_CHANNEL')"
        @click="emit('create')"
      />
      </div>
    </header>

    <div class="px-3 pb-2">
      <label
        class="flex items-center gap-2 px-3 rounded-lg h-9 bg-n-alpha-2 text-n-slate-10 focus-within:ring-1 focus-within:ring-n-brand"
      >
        <span class="flex-shrink-0 size-4 i-lucide-search" />
        <input
          v-model="search"
          type="search"
          class="w-full !m-0 !p-0 text-sm !bg-transparent !border-0 !outline-none text-n-slate-12 placeholder:text-n-slate-10 !shadow-none"
          :placeholder="t('TEAM_CHAT.SEARCH_CHANNELS')"
        />
      </label>
    </div>

    <nav class="flex-1 min-h-0 px-2 pb-3 overflow-y-auto">
      <p
        class="px-2 pt-3 pb-1.5 text-[0.6875rem] font-semibold tracking-wide uppercase text-n-slate-10"
      >
        {{ t('TEAM_CHAT.CHANNELS') }}
      </p>

      <div v-if="!channels.length" class="px-3 py-6 text-center">
        <span
          class="inline-flex items-center justify-center mb-2 rounded-full size-10 bg-n-alpha-2 text-n-slate-10"
        >
          <span class="size-5 i-lucide-messages-square" />
        </span>
        <p class="text-sm font-medium text-n-slate-12">
          {{ t('TEAM_CHAT.NO_CHANNELS') }}
        </p>
        <p class="mt-0.5 text-xs text-n-slate-11">
          {{
            canCreate
              ? t('TEAM_CHAT.NO_CHANNELS_ADMIN')
              : t('TEAM_CHAT.NO_CHANNELS_AGENT')
          }}
        </p>
      </div>
      <p
        v-else-if="!visibleChannels.length"
        class="px-3 py-4 text-sm text-center text-n-slate-10"
      >
        {{ t('TEAM_CHAT.NO_CHANNEL_FOUND') }}
      </p>

      <button
        v-for="channel in visibleChannels"
        :key="channel.id"
        type="button"
        class="flex items-center w-full gap-2.5 px-2 py-1.5 text-sm text-start rounded-lg transition-colors"
        :class="
          channel.id === activeChannelId
            ? 'bg-n-alpha-2 text-n-slate-12'
            : 'text-n-slate-11 hover:bg-n-alpha-1'
        "
        @click="emit('select', channel)"
      >
        <span
          class="flex items-center justify-center flex-shrink-0 rounded-lg size-7"
          :class="channelColor(channel)"
        >
          <span
            class="size-3.5"
            :class="channel.private ? 'i-lucide-lock' : 'i-lucide-hash'"
          />
        </span>
        <span
          class="flex-1 min-w-0 truncate"
          :class="{
            'font-semibold text-n-slate-12': unreadFor(channel),
            'font-medium': channel.id === activeChannelId,
            'opacity-60': channel.muted && !unreadFor(channel),
          }"
        >
          {{ channel.name }}
        </span>
        <span
          v-if="channel.muted"
          class="flex-shrink-0 size-3.5 i-lucide-bell-off text-n-slate-10"
        />
        <span
          v-else-if="unreadFor(channel)"
          class="grid flex-shrink-0 px-1.5 text-xs font-semibold rounded-full min-w-5 h-5 place-content-center bg-n-brand text-white"
        >
          {{ unreadFor(channel) > 99 ? '99+' : unreadFor(channel) }}
        </span>
      </button>

      <p
        class="px-2 pt-5 pb-1.5 text-[0.6875rem] font-semibold tracking-wide uppercase text-n-slate-10"
      >
        {{ t('TEAM_CHAT.ONLINE_NOW') }} · {{ onlineAgents.length }}
      </p>
      <p v-if="!onlineAgents.length" class="px-2 text-xs text-n-slate-10">
        {{ t('TEAM_CHAT.NO_ONE_ONLINE') }}
      </p>
      <div
        v-for="agent in onlineAgents"
        :key="agent.id"
        class="flex items-center gap-2.5 px-2 py-1 text-sm text-n-slate-11"
      >
        <Avatar
          :src="agent.thumbnail"
          :name="agent.available_name || agent.name"
          :status="agent.availability_status"
          :size="24"
          rounded-full
        />
        <span class="truncate">{{ agent.available_name || agent.name }}</span>
      </div>
    </nav>
  </aside>
</template>
